import { Component, OnDestroy, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { MiPageNavComponent } from '@lenderhub/pages/mi/mi-page-nav/mi-page-nav.component';
import { PowerBiComponent } from '@shared/components/powerbi/powerbi.component';
import { roles } from '@shared/constants';
import { AnalyticsService } from '@shared/services/analytics.service';
import { LenderHubDataService } from '@shared/services/lenderhub-data.service';
import { Subscription } from 'rxjs';

interface IMiGenericReport {
  id: string;
  name: string;
}

interface IEmbedToken {
  token: string;
  tokenId: string;
  expiration: Date;
}

interface IEmbedConfig {
  id: string;
  embedUrl: string;
  embedToken: IEmbedToken;
}

export const miGenericReports = [
  { path: 'affordability-mi', displayName: 'Affordability MI', role: roles.lenderPaidMiRoles.affordabilityMi },
  {
    path: 'affordability-mi-r12',
    displayName: 'Affordability MI R12 Months',
    role: roles.lenderPaidMiRoles.affordabilityMiR12,
  },
  { path: 'completions-mi', displayName: 'Lender Completions MI', role: roles.lenderPaidMiRoles.completionsMi },
  { path: 'bdm-mi-pack-trial', displayName: 'BDM Pack (Trial)', role: roles.lenderPaidMiRoles.bdmMiPackTrial },
  { path: 'criteria-mi', displayName: 'Criteria MI', role: roles.lenderPaidMiRoles.criteriaMi },
  { path: 'criteria-mi-trial', displayName: 'Criteria MI (Trial)', role: roles.lenderPaidMiRoles.criteriaMiTrial },
  { path: 'product-mi', displayName: 'Product MI', role: roles.lenderPaidMiRoles.productMi },
  { path: 'criteria-sandbox', displayName: 'Criteria Sandbox', role: roles.lenderPaidMiRoles.criteriaSandbox },
  { path: 'bdm-mi-pack', displayName: 'BDM pack', role: roles.lenderPaidMiRoles.bdmMiPack },
  {
    path: 'executive-market-summary',
    displayName: 'Executive Market Summary',
    role: roles.lenderPaidMiRoles.executiveMarketSummary,
  },
  { path: 'property-mi', displayName: 'Property', role: roles.lenderPaidMiRoles.propertyMi },
  {
    path: 'lender-ignite-activity',
    displayName: 'Lender Ignite Activity',
    role: roles.lenderPaidMiRoles.lenderIgniteActivity,
  },
];

@Component({
  selector: 'app-generic-mi-report-viewer',
  templateUrl: 'mi-generic-report-viewer.component.html',
  imports: [MiPageNavComponent, FormsModule, PowerBiComponent],
})
export class MiGenericReportViewerComponent implements OnDestroy {
  private readonly analyticsService = inject(AnalyticsService);
  private readonly dataService = inject(LenderHubDataService);

  public miReports: IMiGenericReport[] = [];
  public embedConfig: IEmbedConfig | null = null;
  private reportPath: string = '';
  private readonly routeSub: Subscription;

  public constructor() {
    const activatedRoute = inject(ActivatedRoute);

    this.routeSub = activatedRoute.params.subscribe(params => {
      this.reportPath = params.reportpath;
      this.embedConfig = null;
      this.getReports();
    });
  }

  public ngOnDestroy(): void {
    this.routeSub.unsubscribe();
  }

  public getReports() {
    this.dataService.get<IMiGenericReport[]>(`PowerBi/Report/${this.reportPath}`).subscribe(reports => {
      this.miReports = reports;
      this.reportChange(reports[0].id);

      this.analyticsService.log(this.getReportByPath(this.reportPath)?.displayName);
    });
  }

  public reportChange(reportId: string) {
    this.dataService.get<IEmbedConfig>(`PowerBi/Report/${this.reportPath}/Configuration/${reportId}`).subscribe(config => {
      this.embedConfig = config;
    });
  }

  private getReportByPath(path: string): (typeof miGenericReports)[number] | undefined {
    return miGenericReports.find(x => x.path === path);
  }
}
